import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import { useLocation, useNavigate } from "react-router-dom";
import { getPropertyById, getPlotById } from "../../Services/ApiServices";
import Navbar from "../../Components/Navbar";
import Footer from "../Home/Footer";

const DisclaimerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [propertyData, setPropertyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const searchParams = new URLSearchParams(location.search);
      const projectId = searchParams.get("project");
      const propertyTypeFromUrl = searchParams.get("type");

      if (!projectId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        let response;
        if (propertyTypeFromUrl === "plot") {
          response = await getPlotById(projectId);
          if (response && response.status && response.data) {
            setPropertyData({ ...response.data, isPlot: true });
          }
        } else {
          response = await getPropertyById(projectId);
          if (response && response.status && response.property) {
            setPropertyData(response.property);
          }
        }
      } catch (error) {
        console.error("Failed to fetch property details for disclaimer:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [location.search]);

  if (isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "white" }}>
        <Navbar />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
          }}
        >
          <CircularProgress size={60} sx={{ color: "#FF5722" }} />
        </Box>
      </Box>
    );
  }

  if (!propertyData) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#F9FAFB" }}>
        <Navbar />
        <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Property Not Found
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/realestate")}
            startIcon={<ArrowBackIcon />}
            sx={{ bgcolor: "#FF5722", "&:hover": { bgcolor: "#E64A19" } }}
          >
            Back to Listings
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F9FAFB", display: "flex", flexDirection: "column" }}>
      <Navbar />

      <Container maxWidth="lg" sx={{ py: 6, flexGrow: 1 }}>
        {/* Back Button */}
        <Button
          onClick={() => window.close()}
          startIcon={<ArrowBackIcon />}
          sx={{
            textTransform: "none",
            color: "#4B5563",
            fontFamily: "Inter",
            fontWeight: 600,
            mb: 3,
            "&:hover": { color: "#FF5722", bgcolor: "transparent" },
          }}
        >
          Close Window & Return
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            border: "1px solid #E5E7EB",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.03)",
            bgcolor: "white",
            textAlign: "left",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: "50%",
                bgcolor: "#FFF3F0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <InfoIcon sx={{ color: "#FF5722", fontSize: 28 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "Manrope",
                  fontWeight: 800,
                  color: "#1C1917",
                  fontSize: { xs: "24px", md: "32px" },
                  mb: 1,
                }}
              >
                Project Disclaimer
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: "Inter",
                  color: "#6B7280",
                  fontWeight: 500,
                }}
              >
                For {propertyData.name} by{" "}
                <span style={{ color: "#FF5722", fontWeight: 600 }}>
                  {propertyData.groupName}
                </span>
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Full Disclaimer Content */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontSize: { xs: "15px", md: "16px" },
                color: "#374151",
                lineHeight: 1.8,
                whiteSpace: "pre-line",
                textAlign: "justify",
              }}
            >
              {propertyData.disclaimer || "No disclaimer content available for this property."}
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Additional Legal Note */}
          <Box
            sx={{
              p: 2.5,
              bgcolor: "#F9FAFB",
              borderRadius: 3,
              borderLeft: "4px solid #FF5722",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: "#1C1917",
                fontFamily: "Inter",
                mb: 0.5,
              }}
            >
              Important Notice
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter",
                color: "#6B7280",
                lineHeight: 1.6,
              }}
            >
              All property information, including dimensions, layout maps, amenities, and price estimations, is collected from developers and public domains. Clients are strongly advised to verify the project credentials and RERA registration directly with the builder or state regulatory authorities before making any financial commitment.
            </Typography>
          </Box>
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default DisclaimerPage;
